import {
  BILLING_LIMITS,
  type ClearSettingRequest,
  type SettingDefinitionMetadata,
  type SettingDetailDto,
  type WriteSettingRequest,
} from '@aysar/contracts';
import { useCallback, useMemo } from 'react';

import { useHasPermission } from '@/shared/hooks/useHasPermission/useHasPermission';
import { useAppSettingsQuery } from '@/shared/services/app-settings/app-settings.api';
import {
  useClearSettingMutation,
  useSettingsDetailsQuery,
  useSettingsRegistryQuery,
  useWriteSettingMutation,
} from '@/shared/services/settings/settings.api';
import { toApiError } from '@/shared/utils/transform-error/transform-error';
import { type ApiError } from '@/shared/utils/transform-error/transform-error.types';

import {
  type DirectPriceMap,
  withPrice,
  withoutPrice,
} from '../../utils/direct-price-map/direct-price-map.util';
import {
  DIRECT_PRICE_SCOPE_IDS,
  type DirectPriceScopeId,
  type EffectivePrice,
  effectivePriceOf,
  scopeOfId,
  storedPricesOf,
} from '../../utils/direct-price-scopes/direct-price-scopes.util';
import { definitionOf } from '../../utils/registry-definition/registry-definition.util';

/** The key this hook edits — gender-scoped, and owned by this module (P10.4). */
export const DIRECT_PRICES_KEY = 'billing.directPrices';

/** What each tier unlocks — the matrix's key, and the tier vocabulary itself. */
export const TIER_CAPABILITIES_KEY = 'billing.tierCapabilities';

/** What each tier overrides numerically — the lever board's key (P26a). */
export const TIER_LIMITS_KEY = 'billing.tierLimits';

/**
 * Every key this page reads, in one request.
 *
 * `useSettingsDetailsQuery` caches per argument, so every hook on this page that
 * passes this same string shares one cache entry and one round trip —
 * `usePlanMode`, `useTierCapabilities` and `useTierLimits` all pass it.
 *
 * `BILLING_LIMITS` is spread in although those keys are not in the `billing`
 * group: a lever a tier can override is read here for the number it **inherits**
 * when the tier does not, and that base is the only thing on screen telling an
 * operator what an empty lever box is worth. Fetching it separately would be a
 * second round trip for a sentence beside a box.
 */
export const BILLING_SETTING_KEYS = [
  DIRECT_PRICES_KEY,
  'billing.planMode',
  TIER_CAPABILITIES_KEY,
  TIER_LIMITS_KEY,
  ...BILLING_LIMITS,
].join(',');

/** What the board can say about itself. `restricted` is an ANSWER, not an error. */
export type DirectPricesState = 'restricted' | 'loading' | 'error' | 'ready';

/** One product's price at every scope, as the operator wants to leave it. */
export type DirectPriceDraft = Readonly<Record<DirectPriceScopeId, number | null>>;

export interface UseDirectPrices {
  state: DirectPricesState;
  error: ApiError | null;
  refetch: () => void;
  /**
   * ISO 4217 CODE — never a glyph, which garbles under bidi (P10.4).
   *
   * From `/app-settings`, which needs no permission, so an amount is never drawn
   * without the unit it is quoted in. `''` while it is still loading; a consumer
   * withholds the amount rather than pairing it with nothing.
   */
  currency: string;
  /** The definition, so a consumer honours `dangerous` rather than assuming it. */
  definition: SettingDefinitionMetadata | null;
  /** `settings.manage` — the key `PUT /admin/settings/:key` actually checks. */
  mayManage: boolean;
  /** The override stored at one scope. What a write is composed from. */
  storedFor: (scope: DirectPriceScopeId) => DirectPriceMap;
  /** What one product costs at one scope right now, and which layer decided. */
  priceFor: (productKey: string, scope: DirectPriceScopeId) => EffectivePrice;
  /**
   * Leaves one product priced exactly as the draft says, touching only the
   * scopes that actually moved. Rejects with an `ApiError`.
   */
  savePrices: (productKey: string, draft: DirectPriceDraft) => Promise<void>;
  isSaving: boolean;
}

/**
 * `billing.directPrices`, read and written from the module that owns its editor.
 *
 * ## Three facts, and none of them derivable from the others
 *
 * The **coded default** comes from the registry (`defaultValue`), the **stored
 * override** and the **effective** value from the batch details endpoint.
 * `effective` is the resolution chain already applied, so an override cannot be
 * recovered from it — which is exactly why the write below composes from
 * `stored`. This is `useAiPricing`'s shape (P10.5d2) with one difference that
 * changes everything about it: `ai.pricing` is global-only, and this key is
 * **gender-scoped**, so there are three independent maps rather than one and the
 * inheritance between them is the thing the surface exists to show.
 *
 * ## Why writing the effective map would be a silent policy change
 *
 * A male row composed from what men currently resolve to would copy the global
 * price of **every** product into the male override. Nothing would look wrong:
 * the numbers on screen would be identical. But a later change to the global
 * price would then reach nobody, because every product now has a gender-scoped
 * value shadowing it — a pricing decision that silently did not apply, which is
 * the failure N25(b) describes for caches and this rule describes for scopes.
 *
 * ## Last write wins, and that is recorded rather than hidden
 *
 * `PUT /admin/settings/:key` replaces the scope's whole value, so two operators
 * pricing two different products at the same moment will have one overwrite the
 * other. The write is composed from the freshest map the cache holds (each
 * mutation invalidates the `Setting` tag, so the batch is refetched after every
 * save), which narrows the window to one round trip — it does not close it. The
 * real fix is a precondition stamp on the settings write, which is a
 * settings-wide backend concern touching every key and must not be invented
 * inside a price table.
 */
export function useDirectPrices(): UseDirectPrices {
  const { can } = useHasPermission();
  const mayRead = can('settings.read');
  const mayManage = can('settings.manage');

  // Skipped, never fired-and-refused: a request guaranteed to 403 costs a round
  // trip, logs an error and teaches the operator nothing.
  const registry = useSettingsRegistryQuery(undefined, { skip: !mayRead });
  const details = useSettingsDetailsQuery({ keys: BILLING_SETTING_KEYS }, { skip: !mayRead });
  // Not from the settings batch: the currency needs NO permission (it is
  // published on `/app-settings`), and reading it here would make one fact
  // depend on a key the fact does not require.
  const { data: appSettings } = useAppSettingsQuery();

  const [writeSetting, writeState] = useWriteSettingMutation();
  const [clearSetting, clearState] = useClearSettingMutation();

  const definition = useMemo(() => definitionOf(registry.data, DIRECT_PRICES_KEY), [registry.data]);

  const detail = useMemo(
    () => details.data?.items.find((item) => item.key === DIRECT_PRICES_KEY),
    [details.data],
  );

  const storedFor = useCallback(
    (scope: DirectPriceScopeId) => storedPricesOf(detail, scope),
    [detail],
  );

  const priceFor = useCallback(
    (productKey: string, scope: DirectPriceScopeId) => effectivePriceOf(detail, scope, productKey),
    [detail],
  );

  const savePrices = useCallback(
    async (productKey: string, draft: DirectPriceDraft) => {
      try {
        for (const id of DIRECT_PRICE_SCOPE_IDS) {
          const current = storedPricesOf(detail, id)[productKey] ?? null;
          const next = draft[id];

          if (next === current) {
            // Untouched. Writing it anyway would take a scope that inherits and
            // give it an override holding the number it already resolved to.
            continue;
          }

          await applyScope(detail, id, productKey, next, writeSetting, clearSetting);
        }
      } catch (error) {
        throw toApiError(error);
      }
    },
    [detail, writeSetting, clearSetting],
  );

  const loaded = !registry.isLoading && !details.isLoading;

  const requestError =
    registry.error !== undefined
      ? toApiError(registry.error)
      : details.error === undefined
        ? null
        : toApiError(details.error);

  /**
   * A server that declares no `billing.directPrices` is an error with an ERROR
   * OBJECT, not a `state` of error beside a `null` one: consumers hand `error`
   * to `RenderGate`, which renders its children when there is none — and the
   * board would then draw editable boxes over a key nothing can store
   * (`useAiPricing`'s exact defect).
   */
  const missingDefinition: ApiError | null =
    mayRead && loaded && requestError === null && definition === null
      ? { status: 0, code: 'setting_not_declared', message: DIRECT_PRICES_KEY }
      : null;

  const error = requestError ?? missingDefinition;

  const state: DirectPricesState = !mayRead
    ? 'restricted'
    : !loaded
      ? 'loading'
      : error !== null
        ? 'error'
        : 'ready';

  return {
    state,
    error,
    refetch: () => {
      void registry.refetch();
      void details.refetch();
    },
    currency: appSettings?.['billing.currency'] ?? '',
    definition,
    mayManage,
    storedFor,
    priceFor,
    savePrices,
    isSaving: writeState.isLoading || clearState.isLoading,
  };
}

/**
 * The two settings mutations, described by what this file uses of them.
 *
 * Structural rather than imported from RTK Query: the trigger's own type is a
 * generic six deep, and naming it here would couple a price helper to the
 * shape of a query-builder rather than to the request it sends.
 */
type WriteTrigger = (args: { key: string; body: WriteSettingRequest }) => {
  unwrap: () => Promise<unknown>;
};

type ClearTrigger = (args: { key: string; body: ClearSettingRequest }) => {
  unwrap: () => Promise<unknown>;
};

/** One scope brought to the drafted amount — a write, or a clear when it empties. */
async function applyScope(
  detail: SettingDetailDto | undefined,
  id: DirectPriceScopeId,
  productKey: string,
  amount: number | null,
  writeSetting: WriteTrigger,
  clearSetting: ClearTrigger,
): Promise<void> {
  const stored = storedPricesOf(detail, id);
  const scope = scopeOfId(id);

  if (amount === null) {
    const next = withoutPrice(stored, productKey);

    if (Object.keys(next).length === 0) {
      // The last override in this scope: CLEAR the row rather than storing `{}`,
      // so inheritance visibly returns instead of the scope reading as
      // "overridden with nothing".
      await clearSetting({ key: DIRECT_PRICES_KEY, body: scope }).unwrap();

      return;
    }

    await writeSetting({ key: DIRECT_PRICES_KEY, body: { ...scope, value: next } }).unwrap();

    return;
  }

  await writeSetting({
    key: DIRECT_PRICES_KEY,
    body: { ...scope, value: withPrice(stored, productKey, amount) },
  }).unwrap();
}
